import { Link } from "react-router-dom";
import PageBanner from "../PageBanner";
import SocialMediaSidebar from "../SocialMediaSidebar";
import SectionHeaderNoPDF from "../SectionHeaderNoPDF";
import React from "react";
import {
  FaWifi,
  FaChartLine,
  FaBook,
  FaLaptopCode,
  FaBroadcastTower,
  FaDatabase,
  FaCogs,
  FaUsersCog,
} from "react-icons/fa";
import { BsFillGearFill } from "react-icons/bs";
import gyanYantram1 from "../../assets/images/gyanYantram.png";
import gyanYantram2 from "../../assets/images/gyanYnatram1.png";
import ScrollToTopButton from "../ScrollToTopButton";

const features = [
  {
    icon: <FaBook className="text-[#00D084] text-8xl pt-4" />,
    title: "Digital Learning Hub",
    description:
      "Enables access to multilingual e-learning content, government schemes, and skill-based programs in remote areas.",
  },
  {
    icon: <FaLaptopCode className="text-blue-500 text-8xl pt-4" />,
    title: "Real-time Data Collection",
    description:
      "Captures field data from users and locations for analytics, reporting, and decision-making across education and governance.",
  },
  {
    icon: <FaBroadcastTower className="text-yellow-600 text-8xl pt-4" />,
    title: "Wireless Connectivity",
    description:
      "Equipped with 4G/5G/Wi-Fi modules to work in offline/online modes, ensuring seamless operation even in low-connectivity zones.",
  },
  {
    icon: <FaChartLine className="text-purple-600 text-8xl pt-4" />,
    title: "IoT Integration",
    description:
      "Integrates with sensors and field devices to gather real-time environmental or device-specific information for actionable insights.",
  },
  {
    icon: <FaDatabase className="text-teal-600 text-8xl pt-4" />,
    title: "Cloud Connectivity",
    description:
      "Syncs with central servers and cloud platforms for data aggregation, backup, and cross-platform access to services.",
  },
  {
    icon: <FaUsersCog className="text-gray-700 text-8xl pt-4" />,
    title: "User-Centric Interface",
    description:
      "Simple, multilingual interface with biometric and mobile-based login options, designed for rural and semi-urban demographics.",
  },
];

const GyanYantram = () => {
  return (
    <>
      <div>
        <SocialMediaSidebar />
      </div>
      <PageBanner
        title="GyanYantram"
        breadcrumb={[
          { label: "Home", href: "/" },
          { label: "Platform" },
          { label: "GyanYantram" },
        ]}
      />
      <div className="max-w-6xl mx-auto p-4">
        <SectionHeaderNoPDF
          title="GyanYantram @ AJEEVI"
          shareUrl="https://gyanyantram.ajeevi.in/gyanyantram"
        />
      </div>
      <div>
        <ScrollToTopButton />
      </div>

      {/* Introduction Section */}
      <div className="bg-white w-full px-4 sm:px-6 md:px-10 lg:px-28 py-10 text-justify">
        <div className="flex flex-col-reverse xl:flex-row items-center gap-10">
          <div className="w-full xl:w-7/12">
            <h1 className="text-4xl font-bold text-[#00D084] mb-4">
              What is GyanYantram?
            </h1>
            <h5 className="text-xl font-medium mb-4 text-black">
              GyanYantram is a portable, rugged, IoT-enabled device that acts as a digital gateway for delivering educational, governance, and survey-based services in the last-mile areas.
            </h5>
            <p className="text-black mb-4 text-lg">
              Designed to empower citizens, especially in remote and underserved regions, it serves as a digital facilitator — connecting people to government schemes, digital literacy resources, and centralized databases.
            </p>
            <p className="text-black mb-6 text-lg">
              The device operates in hybrid mode (online/offline), ensuring uninterrupted service delivery and real-time monitoring for administrators. It combines data acquisition, processing, and cloud integration to bridge the digital divide.
            </p>

            {/* Capabilities */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <FaCogs className="text-[#00D084] text-[120px]" />
                <div>
                  <h4 className="text-xl font-bold text-black mb-1">
                    Multi-Service Enablement
                  </h4>
                  <p className="text-black text-lg">
                    Supports e-governance, education, health, agriculture data collection, grievance handling, and real-time communication.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <BsFillGearFill className="text-[#00D084] text-[130px]" />
                <div>
                  <h4 className="text-xl font-bold text-black mb-1">
                    Field Deployable
                  </h4>
                  <p className="text-black text-lg">
                    Lightweight and durable design makes it ideal for field surveys, community outreach, and remote rural applications.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="w-full xl:w-5/12 relative">
            <div className="rounded overflow-hidden">
              <img
                src={gyanYantram1}
                alt="GyanYantram Deployment"
                className="w-full h-auto object-cover rounded-t"
              />
              <img
                src={gyanYantram2}
                alt="GyanYantram Device"
                className="w-full h-auto object-cover rounded-b"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="w-full bg-white px-4 sm:px-6 md:px-10 lg:px-28 py-10 text-justify">
        <div className="text-center max-w-4xl mx-auto mb-12">
          <h4 className="text-[#00D084] text-2xl font-semibold mb-2">
            Our Features
          </h4>
          <h1 className="text-3xl md:text-5xl font-bold mb-4">
            Smart, Portable, and Purpose-Built for Rural Empowerment
          </h1>
          <p className="text-black text-lg">
            GyanYantram delivers powerful educational and government services to remote areas, ensuring no citizen is left behind in the digital revolution.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-gray-100 rounded shadow hover:shadow-lg transition p-6 text-center"
            >
              <div className="flex justify-center items-center mb-4">
                {feature.icon}
              </div>
              <h4 className="text-2xl font-semibold text-gray-800 mb-3">
                {feature.title}
              </h4>
              <p className="text-black text-lg">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default GyanYantram;


